import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiClient from '../../services/apiService';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Tooltip,
  SelectChangeEvent,
  Snackbar,
  Alert as MuiAlert,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SecurityIcon from '@mui/icons-material/Security';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';

// Define interfaces
interface User {
  id: number;
  username: string;
  email: string;
  full_name?: string;
  is_active: boolean;
  is_admin: boolean;
  role: string;
  created_at: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface UserListResponse {
  items: User[];
  total: number;
  page: number;
  size: number;
}

// Component for creating a new user
const CreateUserDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();

  // Define role options - Super Admin only selectable by a Super Admin
  const roleOptions = [
    { value: 'admin', label: t('users.admin'), icon: <AdminPanelSettingsIcon /> },
    { value: 'operator', label: t('users.operator'), icon: <PersonIcon /> },
    ...(currentUser?.role === 'ingecom'
      ? [{ value: 'ingecom', label: t('users.superAdmin'), icon: <SecurityIcon /> }]
      : [])
  ];

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    role: 'operator',
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validatePassword = (pwd: string): string | null => {
    const minLength = parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6');
    
    if (pwd.length < minLength) {
      return t('users.passwordMinLength', { minLength });
    }
    
    if (!/[A-Z]/.test(pwd)) {
      return t('users.passwordMustHaveUppercase') || 'Le mot de passe doit contenir au moins une majuscule';
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      return t('users.passwordMustHaveSpecialChar') || 'Le mot de passe doit contenir au moins un caractère spécial';
    }
    
    return null;
  };

  // Reset form data when dialog opens
  React.useEffect(() => {
    if (open) {
      setFormData({
        username: '',
        email: '',
        password: '',
        full_name: '',
        role: 'operator',
      });
      setConfirmPassword('');
      setError(null);
    }
  }, [open]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name || 'role']: value }));
  };

  const handleSubmit = async () => {
    if (!formData.username || !formData.email || !formData.password) {
      setError(
        t('users.required', {
          field: t('users.username') + ', ' + t('users.email') + ', ' + t('users.password'),
        })
      );
      return;
    }

    if (formData.password !== confirmPassword) {
      setError(t('users.passwordsDoNotMatch'));
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await apiClient.post('/auth/register', formData);
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error creating user:', err);
      const errorMessage = err.response?.data?.detail || t('users.createError');
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('users.createNewUser')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
          <TextField
            label={t('users.username')}
            name="username"
            value={formData.username}
            onChange={handleTextChange}
            fullWidth
            required
          />
          <TextField
            label={t('users.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleTextChange}
            fullWidth
            required
          />
          <TextField
            label={t('users.password')}
            name="password"
            type="password"
            value={formData.password}
            onChange={handleTextChange}
            fullWidth
            required
            helperText={t('users.passwordRequirements', {
              minLength: parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6')
            }) || `Minimum ${parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6')} caractères, 1 majuscule, 1 caractère spécial`}
            inputProps={{
              autoComplete: 'new-password',
              onCopy: (e) => e.preventDefault(),
            }}
          />
          <TextField
            label={t('users.confirmPassword')}
            name="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            required
            error={formData.password !== confirmPassword && confirmPassword !== ''}
            helperText={
              formData.password !== confirmPassword && confirmPassword !== ''
                ? t('users.passwordsDoNotMatch')
                : ''
            }
            inputProps={{
              autoComplete: 'new-password',
              onCopy: (e) => e.preventDefault(),
            }}
          />
          <TextField
            label={t('users.fullName')}
            name="full_name"
            value={formData.full_name}
            onChange={handleTextChange}
            fullWidth
          />
          <FormControl fullWidth>
            <InputLabel>{t('users.role')}</InputLabel>
            <Select
              name="role"
              value={formData.role}
              label={t('users.role')}
              onChange={handleSelectChange}
            >
              {roleOptions.map((role) => (
                <MenuItem key={role.value} value={role.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {role.icon}
                    {role.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('users.cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t('users.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Component for changing user role
const ChangeRoleDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
}> = ({ open, onClose, onSuccess, user }) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [role, setRole] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Define role options - Super Admin only selectable by a Super Admin
  const roleOptions = [
    { value: 'admin', label: t('users.admin'), icon: <AdminPanelSettingsIcon /> },
    { value: 'operator', label: t('users.operator'), icon: <PersonIcon /> },
    ...(currentUser?.role === 'ingecom'
      ? [{ value: 'ingecom', label: t('users.superAdmin'), icon: <SecurityIcon /> }]
      : [])
  ];

  useEffect(() => {
    if (open) {
      setError(null);
      if (user) {
        setRole(user.role);
      }
    }
  }, [open, user]);

  const handleChange = (e: SelectChangeEvent) => {
    setRole(e.target.value);
  };

  const handleSubmit = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);
      
      // Log the data being sent
      console.log('Sending role update with role:', role);
      
      // Try with role as a URL parameter
      await apiClient.put(`/auth/users/${user.id}/role?role=${encodeURIComponent(role)}`, {});
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error updating role:', err);
      // Check for specific error message and translate it
      const errorMessage = err.response?.data?.detail;
      if (errorMessage === "Cannot change your own role") {
        setError(t('users.cannotChangeOwnRole'));
      } else {
        // For other errors
        setError(typeof errorMessage === 'string' ? errorMessage : t('users.updateError'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('users.changeRole')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
          <Typography variant="body1">
            <strong>{t('users.user')}:</strong> {user.username}
          </Typography>
          <FormControl fullWidth>
            <InputLabel>{t('users.role')}</InputLabel>
            <Select
              value={role}
              label={t('users.role')}
              onChange={handleChange}
            >
              {roleOptions.map(roleOption => (
                <MenuItem key={roleOption.value} value={roleOption.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {roleOption.icon}
                    {roleOption.label}
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('users.cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || role === user.role}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t('users.updateRole')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Component for changing user password
const ChangePasswordDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
}> = ({ open, onClose, onSuccess, user }) => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const validatePassword = (pwd: string): string | null => {
    const minLength = parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6');
    
    if (pwd.length < minLength) {
      return t('users.passwordMinLength', { minLength });
    }
    
    if (!/[A-Z]/.test(pwd)) {
      return t('users.passwordMustHaveUppercase') || 'Le mot de passe doit contenir au moins une majuscule';
    }
    
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
      return t('users.passwordMustHaveSpecialChar') || 'Le mot de passe doit contenir au moins un caractère spécial';
    }
    
    return null;
  };

  const handleSubmit = async () => {
    if (!user) return;
    
    if (password !== confirmPassword) {
      setError(t('users.passwordsDoNotMatch'));
      return;
    }

    const passwordError = validatePassword(password);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await apiClient.put(`/auth/users/${user.id}/password`, {
        new_password: password,
        confirm_password: confirmPassword,
      });
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error changing password:', err);
      const errorMessage = err.response?.data?.detail || t('users.updateError');
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('users.changePassword')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
          <Typography variant="body1">
            <strong>{t('users.user')}:</strong> {user.username}
          </Typography>
          <TextField
            label={t('users.newPassword')}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            fullWidth
            required
            helperText={t('users.passwordRequirements', {
              minLength: parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6')
            }) || `Minimum ${parseInt(process.env.REACT_APP_PASSWORD_MIN_LENGTH || '6')} caractères, 1 majuscule, 1 caractère spécial`}
          />
          <TextField
            label={t('users.confirmPassword')}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            required
            error={password !== confirmPassword && confirmPassword !== ''}
            helperText={password !== confirmPassword && confirmPassword !== '' ? t('users.passwordsDoNotMatch') : ''}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('users.cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !password || password !== confirmPassword}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t('users.changePassword')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Component for changing user's full name
const ChangeFullNameDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
}> = ({ open, onClose, onSuccess, user }) => {
  const { t } = useTranslation();
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && user) {
      setFullName(user.full_name || '');
      setError(null);
    }
  }, [open, user]);

  const handleSubmit = async () => {
    if (!user) return;
    if (!fullName.trim()) {
      setError(t('users.fullNameRequired') || 'Le nom complet est requis');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await apiClient.put(`/auth/users/${user.id}/full_name`, { full_name: fullName });
      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Error updating full name:', err);
      const errorMessage = err.response?.data?.detail || t('users.updateError');
      setError(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
    } finally {
      setLoading(false);
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('users.changeFullName')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, my: 2 }}>
          <Typography variant="body1">
            <strong>{t('users.user')}:</strong> {user.username}
          </Typography>
          <TextField
            label={t('users.fullName')}
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            fullWidth
            required
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('users.cancel')}</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || fullName.trim() === user.full_name}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {t('users.updateFullName')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Main UserList component
const UserList: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { t } = useTranslation();
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [fullNameDialogOpen, setFullNameDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  
  // Delete status
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Define role options (display lookup only, e.g. the role Chip in the
  // table - "ingecom" is included unconditionally since the backend already
  // decides whether Super Admin rows are returned to this viewer at all)
  const roleOptions = [
    { value: 'admin', label: t('users.admin'), icon: <AdminPanelSettingsIcon /> },
    { value: 'operator', label: t('users.operator'), icon: <PersonIcon /> },
    { value: 'ingecom', label: t('users.superAdmin'), icon: <SecurityIcon /> }
  ];

  // Fetch users
  const fetchUsers = useCallback(async () => {
    if (!isAdmin) {
      setError(t('users.permissionDenied'));
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Fetch users from the API - the backend already excludes Super Admin
      // (ingecom) accounts from the response unless the caller is themselves
      // a Super Admin, so no client-side filtering is needed here anymore.
      const response = await apiClient.get<User[]>(`/auth/users`);

      // Set users and total count for pagination
      setTotalCount(response.data.length);
      const start = page * rowsPerPage;
      const end = start + rowsPerPage;
      setUsers(response.data.slice(start, end));
    } catch (err: any) {
      console.error('Error fetching users:', err);
      setError(err.response?.data?.detail || t('common.failedToLoad', { resource: t('users.list').toLowerCase() }));
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, isAdmin, t]);

  // Initial data load
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  // Handle page change
  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle role change
  const handleRoleChange = (user: User) => {
    setSelectedUser(user);
    setRoleDialogOpen(true);
  };

  // Handle password change
  const handlePasswordChange = (user: User) => {
    setSelectedUser(user);
    setPasswordDialogOpen(true);
  };

  // Handle full name change
  const handleFullNameChange = (user: User) => {
    setSelectedUser(user);
    setFullNameDialogOpen(true);
  };

  // Handle user deletion
  const handleDeleteUser = async (user: User) => {
    
    try {
      setDeleteStatus('loading');
      
      await apiClient.delete(`/auth/users/${user.id}`);
      setUsers((prevUsers) => prevUsers.filter((u) => u.id !== user.id));
      setTotalCount((prevCount) => prevCount - 1);

      setDeleteStatus('success');
      setSnackbarMessage(t('users.deleteSuccess', { username: user.username }) as string || 'User deleted successfully');

      // Close dialog
      setDeleteDialogOpen(false);

      // Refetch users to ensure the list is up-to-date
      fetchUsers();
    } catch (err: any) {
      console.error('Error deleting user:', err);
      setDeleteStatus('error');
      setSnackbarMessage(err.response?.data?.detail || t('users.deleteError'));
    }
  };

  // Handle open delete user dialog
  const handleOpenDeleteUserDialog = () => {
    setDeleteDialogOpen(true);
  };

  // Handle close delete user dialog
  const handleCloseDeleteUserDialog = () => {
    setDeleteDialogOpen(false);
  };

  // Get role display
  const getRoleDisplay = (role: string) => {
    const roleOption = roleOptions.find(r => r.value === role);
    return roleOption ? (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {roleOption.icon}
        <Typography variant="body2">{roleOption.label}</Typography>
      </Box>
    ) : role;
  };

  if (loading && users.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'calc(100vh - 64px)',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {t('users.permissionDenied')}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('users.management')}</Typography>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('users.create')}
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('users.username')}</TableCell>
                <TableCell>{t('users.email')}</TableCell>
                <TableCell>{t('users.fullName')}</TableCell>
                <TableCell>{t('users.role')}</TableCell>
                <TableCell>{t('users.status')}</TableCell>
                <TableCell align="right">{t('users.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {loading ? t('users.loading') : t('users.noUsersFound')}
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.full_name || '—'}</TableCell>
                    <TableCell>{getRoleDisplay(user.role)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.is_active ? t('users.active') : t('users.inactive')} 
                        color={user.is_active ? 'success' : 'error'} 
                        size="small" 
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        {isAdmin && (
                          <Tooltip title={t('users.changeFullName')}>
                            <IconButton
                              size="small"
                              onClick={() => handleFullNameChange(user)}
                            >
                              <DriveFileRenameOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAdmin && (
                          <Tooltip title={t('users.changePassword')}>
                            <IconButton
                              size="small"
                              onClick={() => handlePasswordChange(user)}
                            >
                              <LockIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAdmin && (
                          <Tooltip title={t('users.changeRole')}>
                            <IconButton
                              size="small"
                              onClick={() => handleRoleChange(user)}
                            >
                              <SupervisedUserCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAdmin && (
                          <Tooltip title={t('users.delete')}>
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedUser(user);
                                handleOpenDeleteUserDialog();
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) => 
            `${from}-${to} ${t('pagination.of')} ${count}`
          }
          getItemAriaLabel={(type) => {
            switch (type) {
              case 'first':
                return t('pagination.firstPage');
              case 'last':
                return t('pagination.lastPage');
              case 'next':
                return t('pagination.nextPage');
              case 'previous':
                return t('pagination.previousPage');
              default:
                return '';
            }
          }}
        />
      </Paper>

      {/* Dialogs */}
      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={fetchUsers}
      />
      
      <ChangeRoleDialog
        open={roleDialogOpen}
        onClose={() => setRoleDialogOpen(false)}
        onSuccess={fetchUsers}
        user={selectedUser}
      />
      
      <ChangePasswordDialog
        open={passwordDialogOpen}
        onClose={() => setPasswordDialogOpen(false)}
        onSuccess={fetchUsers}
        user={selectedUser}
      />

      <ChangeFullNameDialog
        open={fullNameDialogOpen}
        onClose={() => setFullNameDialogOpen(false)}
        onSuccess={fetchUsers}
        user={selectedUser}
      />

      {/* Delete User Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleCloseDeleteUserDialog}>
          <DialogTitle>{t('users.deleteUser')}</DialogTitle>
          <DialogContent>
            <Typography>
              {t('users.deleteUserConfirm')}
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteUserDialog}>
              {t('common.cancel')}
            </Button>
            <Button 
              onClick={() => selectedUser && handleDeleteUser(selectedUser)} 
              color="error" 
              variant="contained"
              disabled={deleteStatus === 'loading'}
            >
              {deleteStatus === 'loading' ? <CircularProgress size={24} /> : t('common.delete')}
            </Button>
          </DialogActions>
        </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={deleteStatus === 'success' || deleteStatus === 'error'}
        autoHideDuration={6000}
        onClose={() => setDeleteStatus('idle')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setDeleteStatus('idle')} 
          severity={deleteStatus === 'success' ? 'success' : 'error'}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserList;